import { TableCell, TableRow } from "@/lib/components/Table";
import { IconCopy, IconTrash } from "@tabler/icons-react";
import Avatar from "@/lib/components/Avatar";
import { useState } from "react";
import { TeamRole, TeamRoles } from "@repo/api/team-roles";
import { Button } from "@/lib/components/Button/Button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/lib/components/Select";
import { Badge } from "@/lib/components/Badge";
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/lib/components/Dialog";
import { StatusType } from "./TeamTable";
import { copyToClipboard } from "@/lib/utils";
import { paths } from "@/App";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@/lib/components/Tooltip";

type MemberItem = {
  item: {
    id: string;
    fullname?: string;
    role: TeamRole;
    email: string;
  };
  isMobile?: boolean;
  status: StatusType;
  isSelf?: boolean;
  userIsAllowedToEdit: boolean;
  onRoleChange: (itemId: string, role: TeamRole) => void;
  onDelete: (itemId: string) => void;
};

function getRedirectUrl(inviteExternalId: string) {
  const origin = window.location.origin;
  const url = `${origin}${paths.invite(inviteExternalId)}`;

  return url;
}

export function MemberItem({
  isMobile,
  item,
  status,
  isSelf,
  userIsAllowedToEdit,
  onRoleChange,
  onDelete,
}: MemberItem) {
  const [open, setOpen] = useState(false);

  const name = (
    <div className="flex items-center gap-2">
      <Avatar
        text={
          item.fullname ? item.fullname.slice(0, 1) : item.email.slice(0, 1)
        }
      />
      <div className="flex w-full flex-col">
        <span className="line-clamp-1 text-sm font-semibold text-ellipsis">
          {item.fullname}
        </span>
        <span className="line-clamp-1 block truncate text-xs text-ellipsis text-text-secondary">
          {item.email}
        </span>
      </div>
    </div>
  );

  const inviteBadge = (
    <div className="flex items-center gap-2">
      <Badge
        variant={status === "Active" ? "success" : "outline"}
        dotPosition="leading"
        showDot
      >
        {status}
      </Badge>
      {status === "Invited" ? (
        <Tooltip>
          <TooltipTrigger asChild>
            <Button
              onClick={() =>
                copyToClipboard(
                  getRedirectUrl(item.id),
                  "Invite link copied to clipboard! Share this link with the invited member.",
                )
              }
              className="group h-10 w-10"
              variant="ghost"
            >
              <IconCopy
                size={16}
                className="text-grey-400 transition-colors group-hover:text-black-400"
              />
            </Button>
          </TooltipTrigger>
          <TooltipContent>Copy invite link</TooltipContent>
        </Tooltip>
      ) : null}
    </div>
  );

  const memberRole = (
    <div className="flex items-center gap-2 capitalize">
      {userIsAllowedToEdit && !isSelf ? (
        <Select
          value={item.role}
          onValueChange={(value) => onRoleChange(item.id, value as TeamRole)}
        >
          <SelectTrigger className="capitalize" variant="secondary">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {Object.values(TeamRoles).map((role) => (
              <SelectItem key={role} value={role} className="capitalize">
                {role}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      ) : (
        <span className="text-sm text-text-secondary capitalize">
          {item.role}
        </span>
      )}
    </div>
  );

  const deleteMember = (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button size="icon" variant="ghost" className="group min-h-10 min-w-10">
          <IconTrash
            size={16}
            className="text-grey-400 transition-colors group-hover:text-black-400"
          />
        </Button>
      </DialogTrigger>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>
            Delete {status === "Invited" ? "Invite" : "Member"}
          </DialogTitle>
          <DialogDescription>
            Are you sure you want to delete{" "}
            <span className="font-semibold">{item.fullname || item.email}</span>
          </DialogDescription>
        </DialogHeader>
        <DialogFooter className="mt-0">
          <Button
            onClick={() => {
              onDelete(item.id);
              setOpen(false);
            }}
          >
            Delete
          </Button>
          <DialogClose asChild>
            <Button type="button" variant="outline">
              Cancel
            </Button>
          </DialogClose>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );

  if (isMobile) {
    return (
      <div className="rounded-sm border border-border bg-background p-4">
        <div className="flex items-center justify-between gap-2">
          {name}
          {deleteMember}
        </div>
        <div className="mt-4 flex items-center justify-between">
          {inviteBadge}
          {memberRole}
        </div>
      </div>
    );
  }

  return (
    <TableRow className="h-19">
      <TableCell>{name}</TableCell>
      <TableCell>{inviteBadge}</TableCell>
      <TableCell>{memberRole}</TableCell>
      {userIsAllowedToEdit && !isSelf ? (
        <TableCell>{deleteMember}</TableCell>
      ) : null}
    </TableRow>
  );
}
